import { useEffect, useState } from "react";

// Styles
import MorningMeetingCompanyNotesDashboardContainer from "./MorningMeetingCompanyNotesDashboard.styles";

// Font Awesome
import { faDownload } from "@fortawesome/free-solid-svg-icons";

// Utils
import * as Database from "utils/database.utils";
import { createLocalDownload } from "utils/export.utils";

// Components
import Button from "components/Common/Button/Button";
import LoadingSpinner from "components/Common/LoadingSpinner/LoadingSpinner";
import PageTitle from "components/Common/PageTitle/PageTitle";
import MeetingDetailBoxes from "components/Screens/Company/MeetingDetailBoxes/MeetingDetailBoxes";
import SearchOptions from "components/Common/SearchOptions/SearchOptions";
import MorningMeetingCompanyNotes from "../MorningMeetingCompanyNotes/MorningMeetingCompanyNotes";
import { useSettings } from "context/Settings.context";
import { exportFromS3 } from "utils/export.utils";
// React Router
import { useLocation, useHistory } from "react-router-dom";

// Context
import { useBQMQ } from "context/BQMQ.context";

type AnalystType = {
    active: boolean;
    label: string;
    value: string;
};

type FileType = {
    id: string;
    file_name: string;
    deleted: number;
};

type NoteType = {
    date: string;
    notes: string[];
    files: FileType[];
    id: string;
    analyst_id: string;
    archived: number;
    deleted: number;
};

type MorningMeetingMinutesType = {
    id: string;
    portfolio_weight: number;
    bq_score: number;
    mq_score: number;
    investment_thesis: string;
    last_vote_date: string;
    analyst: string;
    redshift_company_id: number;
    historic_notes: NoteType[];
    fair_value: number | null;
    fair_value_upside_pct: number | null;
};

type ExtractionType = {
    extraction_id: number;
    extraction_file_name: string;
};

const MorningMeetingCompanyNotesDashboard = () => {
    const location = useLocation();
    const history = useHistory();

    const [notFound, setNotFound] = useState<boolean>(false);
    const [companyDetails, setCompanyDetails] =
        useState<MorningMeetingMinutesType>({
            id: "",
            portfolio_weight: 0,
            bq_score: 0,
            mq_score: 0,
            investment_thesis: "",
            last_vote_date: "",
            analyst: "",
            redshift_company_id: 0,
            historic_notes: [],
            fair_value: null,
            fair_value_upside_pct: null,
        });

    const { setShowLoadingToast, setToast } = useSettings();
    const [companyId, setCompanyId] = useState<string>("");
    const [companyName, setCompanyName] = useState<string>("");
    const [extractionDetails, setExtractionDetails] =
        useState<ExtractionType>();
    const [analystsList, setAnalystsList] = useState<any>();
    const [filterQuery, setFilterQuery] = useState<any>();
    const [extractionReady, setExtractionReady] = useState(false);
    const { analysts } = useBQMQ();
    const [notes, setNotes] = useState<NoteType[]>(
        companyDetails.historic_notes
    );

    useEffect(() => {
        if (filterQuery) {
            getMorningMeetingNotesFromQuery(companyId, filterQuery);
        } else {
            setNotes(companyDetails.historic_notes);
        }
    }, [filterQuery]);

    useEffect(() => {
        getAnalysts(analysts);
    }, [analysts]);

    const filterOptions = [
        {
            id: 1,
            name: "Analyst",
            value: "analyst",
            type: "DROPDOWN",
            searchable: true,
            options: analystsList,
        },
        {
            id: 2,
            name: "Date Range",
            value: "dateRange",
            type: "DATE_RANGE",
        },
    ];

    const getMorningMeetingMinutesById = async () => {
        setNotFound(false);
        const response = await Database.get(
            `api/bqmq_voting/get-morning-meeting-minutes-by-id/${String(
                companyId
            )}`
        );
        if (response.ok) {
            const data = await response.json();
            setCompanyDetails(data.data[0]);
            setCompanyName(data.data[0].name);
        } else {
            setNotFound(true);
        }
    };

    const getAnalysts = (data: AnalystType[]) => {
        const uniqueAnalystsMap = new Map();

        data.filter((analyst: AnalystType) => analyst.active).forEach(
            (analyst: AnalystType) => {
                if (analyst.label) {
                    uniqueAnalystsMap.set(analyst.label, analyst.value);
                }
            }
        );

        const sortedUniqueAnalysts = Array.from(uniqueAnalystsMap.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([label, value]) => ({
                label,
                value,
            }));

        setAnalystsList(sortedUniqueAnalysts);
    };

    const getMorningMeetingNotesFromQuery = async (
        companyId: string,
        queryString: string
    ) => {
        try {
            const encodedQueryString = encodeURI(queryString);
            const response = await Database.get(
                `api/bqmq_voting/meeting-note/company/${companyId}?${encodedQueryString}`
            );
            const data = await response.json();
            setNotes(data.data);
        } catch (error) {
            console.error("Error fetching meeting notes:", error);
        }
    };

    const getMostRecentExtractionInformation = async () => {
        try {
            const response = await Database.get(
                `api/extractions/extraction_id-by_redshift_id/` +
                    companyDetails.redshift_company_id
            );
            const data = await response.json();
            setExtractionDetails({
                extraction_id: data.data.extraction_id,
                extraction_file_name: data.data.archive_file_path,
            });
        } catch (error) {
            console.error("Failed to fetch extraction information:", error);
        }
    };

    useEffect(() => {
        // Wait for company id to be parsed from URL
        if (!companyId) return;

        setExtractionReady(false);
        getMorningMeetingMinutesById();
        setExtractionReady(true);
    }, [companyId]);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const id = urlParams.get("company_id");
        if (!id) {
            history.replace("/company/morning-meeting-notes");
            return;
        }
        setCompanyId(id);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleOnClick = (
        extractionId_val = extractionDetails?.extraction_id,
        archiveFilePath = extractionDetails?.extraction_file_name
    ) => {
        setShowLoadingToast({ show: true, message: "EXPORTING" });
        exportFromS3(extractionId_val, archiveFilePath).then((res) => {
            setShowLoadingToast({
                show: false,
                message: "Exporting...",
            });
        });
    };

    const handleDownloadPDF = async () => {
        setShowLoadingToast({ show: true, message: "DOWNLOADING" });

        try {
            const response = await Database.get(
                `api/bqmq_voting/download-morning-meeting-minutes-by-id/${companyId}`
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                setToast({
                    show: true,
                    type: "error",
                    message: errorData.message || "Failed to download PDF",
                });
                setShowLoadingToast({ show: false, message: "" });
                return;
            }

            let filename = "morning_meeting_notes.pdf";
            const contentDisposition = response.headers.get(
                "Content-Disposition"
            );
            if (contentDisposition) {
                const filenameMatch =
                    contentDisposition.match(/filename="?([^"]+)"?/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }

            const file = await response.blob();
            createLocalDownload(filename, file);

            setToast({
                show: true,
                type: "success",
                message: "PDF downloaded successfully",
            });
        } catch (error) {
            setToast({
                show: true,
                type: "error",
                message: "An error occurred while downloading the PDF",
            });
        } finally {
            setShowLoadingToast({ show: false, message: "" });
        }
    };

    useEffect(() => {
        if (extractionReady) {
            getMostRecentExtractionInformation();
        }
    }, [companyDetails.redshift_company_id]);

    return (
        <MorningMeetingCompanyNotesDashboardContainer className="pageContainer">
            <div style={{ display: "flex" }}>
                <h1 className="pageTitle">MORNING MEETING NOTES</h1>
            </div>
            <hr />
            {companyDetails.id !== "" && !notFound ? (
                <>
                    <PageTitle
                        title={
                            <div className="resultsTitle">
                                <h1>{companyName}</h1>
                            </div>
                        }
                    />

                    <MeetingDetailBoxes
                        mqScore={companyDetails.mq_score}
                        bqScore={companyDetails.bq_score}
                        investmentThesis={companyDetails.investment_thesis}
                        lastVoteDate={companyDetails.last_vote_date}
                        portfolioWeight={companyDetails.portfolio_weight}
                        analyst={companyDetails.analyst}
                        fairValueUpsidePct={
                            companyDetails.fair_value_upside_pct
                        }
                    />

                    <div className={"item-container"}>
                        <SearchOptions
                            setFilterQuery={setFilterQuery}
                            options={filterOptions}
                            noDebounce
                            useSimpleDates={true}
                        >
                            <Button
                                text={"Excel Model"}
                                color="primary"
                                click={handleOnClick}
                                icon={faDownload}
                            />
                            <Button
                                text={"Download Notes"}
                                color="primary"
                                click={handleDownloadPDF}
                                icon={faDownload}
                            />
                        </SearchOptions>
                    </div>

                    <div className={"morning-notes"}>
                        <MorningMeetingCompanyNotes
                            notes={notes}
                            setNotes={setNotes}
                            backPath={location.pathname + location.search}
                            backLabel="Back to company notes"
                        />
                    </div>
                </>
            ) : !notFound ? (
                <div className={"loadingScreen"}>
                    <LoadingSpinner />
                </div>
            ) : null}
            {notFound && (
                <div className="loadingScreen">
                    <h3 style={{ marginTop: "20px" }}>
                        No morning meetings found for {companyName}
                    </h3>
                </div>
            )}
        </MorningMeetingCompanyNotesDashboardContainer>
    );
};

export default MorningMeetingCompanyNotesDashboard;
